/**
 * Aspect — Unity opening meshes (door / window / curtain)
 * --------------------------------------------------------------
 * Preloads Apartment Modern GLBs exported from Unity FBX and instantiates
 * them for DoorSystem, wall windows, and DecorSystem curtains.
 */

import * as pc from "playcanvas";

/** Native glTF AABB size after export (meters). */
export const OPENING_NATIVE = {
  // Door leaf faces +Z width; thickness on X. Rotate +90° Y so width → local +X (hinge).
  door: { width: 0.828, height: 2.219, thick: 0.14 },
  // Window: width on Z, height on Y, depth on X.
  window: { width: 2.048, height: 1.46, depth: 0.468 },
  // Curtain unit: width on Z, height on Y.
  curtain: { width: 2.412, height: 2.263, depth: 0.366 },
};

const URLS = {
  door: "/assets/models/door.glb?v=tex4",
  window: "/assets/models/window.glb?v=tex3",
  curtain: "/assets/models/curtain.glb?v=tex5",
};

/** Registry cache keys — bump suffix when GLBs are re-exported so stale containers never stick. */
const REG_IDS = {
  door: "opening_door_v4",
  window: "opening_window_v3",
  curtain: "opening_curtain_v5",
};

/** @type {import('../assets/asset_registry.js').AssetRegistry | null} */
let registry = null;
/** @type {Record<string, object>} */
const resolved = {};
/** @type {Promise<void> | null} */
let loadPromise = null;

/**
 * PlayCanvas glTF import often leaves glass opaque / metal chrome-washed.
 * Re-assert materials by mesh name after instantiate.
 * Curtains: preserve fabric albedo/normal maps (never strip textures).
 * Windows/doors: glass polish only — unchanged.
 * @param {pc.Entity} entity
 * @param {string} [assetId]
 */
function polishOpeningMaterials(entity, assetId = "") {
  if (!entity) return;

  /** Only drop Asset wrappers whose GPU resource failed — never wipe a live Texture. */
  const clearBrokenMaps = (mat) => {
    for (const key of ["diffuseMap", "normalMap", "metalnessMap", "glossMap", "emissiveMap", "specularMap", "opacityMap"]) {
      const tex = mat[key];
      if (tex && Object.prototype.hasOwnProperty.call(tex, "resource") && !tex.resource) mat[key] = null;
    }
    if ("sphereMap" in mat && mat.sphereMap && Object.prototype.hasOwnProperty.call(mat.sphereMap, "resource") && !mat.sphereMap.resource) {
      mat.sphereMap = null;
    }
    if ("cubeMap" in mat && mat.cubeMap && Object.prototype.hasOwnProperty.call(mat.cubeMap, "resource") && !mat.cubeMap.resource) {
      mat.cubeMap = null;
    }
  };

  const applyGlass = (mat) => {
    clearBrokenMaps(mat);
    // Clear glass, no emissive — dark corridors must read dark through panes.
    // (Previous soft emissive made door glass look like glowing white panels.)
    mat.useMetalness = true;
    mat.metalness = 0;
    mat.gloss = 0.72;
    mat.opacity = 0.14;
    mat.alphaTest = 0;
    mat.blendType = pc.BLEND_NORMAL;
    mat.depthWrite = false;
    mat.cull = pc.CULLFACE_NONE;
    mat.useSkybox = false;
    mat.useLighting = true;
    mat.useGammaTonemap = true;
    mat.diffuse = new pc.Color(0.12, 0.13, 0.14);
    mat.emissive = new pc.Color(0, 0, 0);
    mat.emissiveIntensity = 0;
    mat.diffuseMap = null;
    mat.emissiveMap = null;
    mat.specularMap = null;
    mat.normalMap = null;
    mat.metalnessMap = null;
    mat.glossMap = null;
    mat.opacityMap = null;
    mat.aoMap = null;
    if ("sphereMap" in mat) mat.sphereMap = null;
    if ("cubeMap" in mat) mat.cubeMap = null;
    if ("specular" in mat) mat.specular = new pc.Color(0.06, 0.06, 0.06);
    // Drop glTF extras that keep a color cast (transmission / sheen / clearcoat).
    if ("chunks" in mat) {
      try {
        mat.chunks = {};
      } catch {
        /* ignore */
      }
    }
    mat.update();
  };

  /** Fabric panels: keep diffuse/normal maps; only tame chrome + lighting flags. */
  const polishCurtainFabric = (mat) => {
    mat.useMetalness = true;
    if ((mat.metalness ?? 0) > 0.25 && !mat.metalnessMap) mat.metalness = 0.02;
    if (mat.gloss == null || mat.gloss > 0.7) mat.gloss = 0.28;
    mat.opacity = 1;
    mat.alphaTest = 0;
    mat.blendType = pc.BLEND_NONE;
    mat.depthWrite = true;
    mat.cull = pc.CULLFACE_NONE;
    mat.useLighting = true;
    mat.useSkybox = false;
    mat.useGammaTonemap = true;
    // Ensure maps stay enabled if present.
    if (mat.diffuseMap) mat.diffuseMapChannel = mat.diffuseMapChannel || "rgb";
    mat.update();
  };

  const visit = (node) => {
    for (const r of node.findComponents?.("render") ?? []) {
      const entries = [];
      if (r.material) entries.push({ mat: r.material, assign: (m) => { r.material = m; }, noShadow: () => { r.castShadows = false; } });
      for (const mi of r.meshInstances ?? []) {
        if (mi.material) entries.push({ mat: mi.material, assign: (m) => { mi.material = m; }, noShadow: () => { mi.castShadow = false; } });
      }
      for (const { mat, assign, noShadow } of entries) {
        const name = String(mat.name || node.name || "").toLowerCase();

        // Curtain asset: never run glass rewrite or map-stripping metal path.
        if (assetId === "curtain") {
          // Curtains often cover the whole window — they must never block the
          // realtime sun, or no light would enter the room at all.
          noShadow();
          if (name.includes("metal")) {
            mat.useMetalness = true;
            mat.metalness = 0.85;
            mat.gloss = 0.55;
            mat.opacity = 1;
            mat.blendType = pc.BLEND_NONE;
            mat.depthWrite = true;
            mat.useSkybox = false;
            // Rod should stay bare metal even if export stamped fabric onto it.
            mat.diffuseMap = null;
            mat.normalMap = null;
            mat.metalnessMap = null;
            mat.diffuse = new pc.Color(0.5, 0.51, 0.54);
            mat.update();
          } else {
            polishCurtainFabric(mat);
          }
          continue;
        }

        clearBrokenMaps(mat);
        // Doors: only named glass gets the see-through treatment. The whole-mesh
        // opacity/blend heuristic was turning the wood leaf invisible, so analyzed
        // doorways looked like empty frames.
        const looksGlass =
          name.includes("glass") ||
          name.includes("mirror") ||
          name.includes("pane") ||
          (assetId === "window" && (mat.blendType === pc.BLEND_NORMAL || (mat.opacity ?? 1) < 0.95));
        if (looksGlass) {
          // Fresh material so no leftover pink tint from the imported glTF shader state.
          const glass = new pc.StandardMaterial();
          glass.name = mat.name || "opening-glass";
          applyGlass(glass);
          assign(glass);
          // Glass panes must let the realtime sun through the window opening.
          noShadow();
        } else if (name.includes("metal")) {
          mat.useMetalness = true;
          mat.metalness = 0.85;
          mat.gloss = 0.55;
          mat.opacity = 1;
          mat.blendType = pc.BLEND_NONE;
          mat.depthWrite = true;
          mat.useSkybox = false;
          mat.diffuse = new pc.Color(0.5, 0.51, 0.54);
          mat.diffuseMap = null;
          mat.update();
        } else {
          // Keep albedo/normal maps; kill accidental full-metal chrome / magenta missing maps.
          mat.useMetalness = true;
          if (mat.metalness > 0.35 && !mat.metalnessMap) mat.metalness = 0.05;
          if (mat.gloss == null || mat.gloss > 0.85) mat.gloss = 0.35;
          mat.opacity = 1;
          mat.blendType = pc.BLEND_NONE;
          mat.depthWrite = true;
          mat.useLighting = true;
          mat.useSkybox = false;
          mat.update();
        }
      }
    }
    for (const child of node.children ?? []) visit(child);
  };
  visit(entity);
}

/**
 * @param {import('../assets/asset_registry.js').AssetRegistry} reg
 */
export function initOpeningAssets(reg) {
  registry = reg;
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    await Promise.all(
      Object.keys(URLS).map(async (id) => {
        const n = OPENING_NATIVE[id];
        const fp = {
          w: n.width ?? n.thick ?? 1,
          h: n.height ?? 1,
          d: n.depth ?? n.thick ?? 0.1,
        };
        try {
          resolved[id] = await registry.ensure(REG_IDS[id], URLS[id], fp, "default");
        } catch (err) {
          console.warn(`[openings] failed to load ${id}`, err);
          resolved[id] = null;
        }
      }),
    );
  })();
  return loadPromise;
}

export function openingAssetsReady() {
  return Boolean(resolved.door || resolved.window || resolved.curtain);
}

export function hasOpeningAsset(id) {
  const d = resolved[id];
  return Boolean(d?.kind === "glb" && d.container?.resource);
}

/** Sync instantiate after initOpeningAssets() has resolved. */
export function instantiateOpening(id) {
  if (!registry) return null;
  const d = resolved[id];
  if (!d || d.kind !== "glb") return null;
  const resource = d.container?.resource;
  if (!resource) return null;
  const entity = registry._instantiateGlb(resource);
  if (entity) {
    entity._aspectOpeningAsset = id;
    entity._aspectKind = "glb";
    polishOpeningMaterials(entity, id);
  }
  return entity;
}

export default {
  OPENING_NATIVE,
  initOpeningAssets,
  openingAssetsReady,
  hasOpeningAsset,
  instantiateOpening,
};
